import React, { useState, useEffect } from 'react';
import api from '../services/api';

function Section({ title, children }) {
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      {children}
    </div>
  );
}

function ConfigField({ label, cle, value, type = 'text', onSave, secret }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value || '');
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    setSaving(true);
    try { await onSave(cle, val); setEditing(false); }
    catch (e) {} finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--ecf-mid-grey)' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--ecf-grey)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      {editing ? (
        <input className="form-input" type={secret ? 'password' : type} value={val}
          onChange={e => setVal(e.target.value)} autoFocus style={{ margin: 0 }} />
      ) : (
        <div style={{ fontSize: '0.88rem', color: value ? 'var(--ecf-black)' : '#bbb' }}>
          {value || '— non configuré —'}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        {editing ? (
          <>
            <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={save} disabled={saving}>
              {saving ? '...' : '✓'}
            </button>
            <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setEditing(false)}>✕</button>
          </>
        ) : (
          <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setEditing(true)}>Modifier</button>
        )}
      </div>
    </div>
  );
}

function TrancheRow({ tranche, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ libelle: tranche.libelle, seuil_min: tranche.seuil_min, seuil_max: tranche.seuil_max || '', ordre: tranche.ordre });

  const save = async () => {
    await onSave(tranche.id, { ...form, seuil_max: form.seuil_max === '' ? null : parseFloat(form.seuil_max) });
    setEditing(false);
  };

  if (editing) return (
    <tr>
      <td><input className="form-input" value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} style={{ margin: 0 }} /></td>
      <td><input className="form-input" type="number" value={form.seuil_min} onChange={e => setForm({ ...form, seuil_min: parseFloat(e.target.value) })} style={{ margin: 0 }} /></td>
      <td><input className="form-input" type="number" value={form.seuil_max} onChange={e => setForm({ ...form, seuil_max: e.target.value })} placeholder="Illimité" style={{ margin: 0 }} /></td>
      <td><input className="form-input" type="number" value={form.ordre} onChange={e => setForm({ ...form, ordre: parseInt(e.target.value) })} style={{ margin: 0, width: 60 }} /></td>
      <td>
        <button className="btn btn-primary" style={{ padding: '3px 8px', fontSize: '0.75rem' }} onClick={save}>✓</button>
        <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: '0.75rem', marginLeft: 4 }} onClick={() => setEditing(false)}>✕</button>
      </td>
    </tr>
  );

  return (
    <tr>
      <td className="important">{tranche.libelle}</td>
      <td>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(tranche.seuil_min)}</td>
      <td>{tranche.seuil_max ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(tranche.seuil_max) : '∞'}</td>
      <td>{tranche.ordre}</td>
      <td>
        <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: '0.75rem' }} onClick={() => setEditing(true)}>Modifier</button>
        <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: '0.75rem', marginLeft: 4, color: 'var(--ecf-error)' }} onClick={() => onDelete(tranche.id)}>Supprimer</button>
      </td>
    </tr>
  );
}

export default function Configuration() {
  const [configs, setConfigs]   = useState([]);
  const [tranches, setTranches] = useState([]);
  const [regions, setRegions]   = useState([]);
  const [testMsg, setTestMsg]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [newTranche, setNewTranche] = useState({ libelle: '', seuil_min: 0, seuil_max: '', ordre: 1 });
  const [newRegion, setNewRegion]   = useState({ numero: '', libelle: '' });
  const [editRegion, setEditRegion] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [c, t, r] = await Promise.all([
        api.get('/configuration/'),
        api.get('/configuration/tranches'),
        api.get('/regions/all'),
      ]);
      setConfigs(c.data);
      setTranches(t.data);
      setRegions(r.data);
    } catch (e) {} finally { setLoading(false); }
  };

  const handleSaveConfig = async (cle, valeur) => {
    await api.put(`/configuration/${cle}`, { valeur });
    loadAll();
  };

  const handleTestSmtp = async () => {
    setTestMsg(null);
    try {
      await api.post('/configuration/smtp/test');
      setTestMsg({ type: 'success', text: 'Email de test envoyé avec succès ✅' });
    } catch (err) {
      setTestMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur SMTP' });
    }
  };

  const handleSaveTranche = async (id, data) => { await api.put(`/configuration/tranches/${id}`, data); loadAll(); };
  const handleDeleteTranche = async (id) => {
    if (!window.confirm('Supprimer cette tranche ?')) return;
    await api.delete(`/configuration/tranches/${id}`); loadAll();
  };
  const handleCreateTranche = async () => {
    await api.post('/configuration/tranches', { ...newTranche, seuil_max: newTranche.seuil_max === '' ? null : parseFloat(newTranche.seuil_max) });
    setNewTranche({ libelle: '', seuil_min: 0, seuil_max: '', ordre: tranches.length + 1 });
    loadAll();
  };
  const handleRecalculer = async () => {
    if (!window.confirm('Recalculer les tranches sur tous les bilans existants ?')) return;
    try {
      await api.post('/configuration/tranches/recalculer');
      alert('Tranches recalculées avec succès sur tous les bilans.');
    } catch (e) { alert('Erreur lors du recalcul'); }
  };

  // ── Régions ──────────────────────────────────────────────
  const handleCreateRegion = async () => {
    if (!newRegion.numero || !newRegion.libelle) return;
    try {
      await api.post('/regions/', { numero: parseInt(newRegion.numero), libelle: newRegion.libelle });
      setNewRegion({ numero: '', libelle: '' });
      loadAll();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };
  const handleUpdateRegion = async (id, libelle, actif) => {
    try {
      await api.put(`/regions/${id}`, { libelle, actif });
      setEditRegion(null);
      loadAll();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };
  const handleDeleteRegion = async (id) => {
    if (!window.confirm('Désactiver cette région ?')) return;
    try {
      await api.delete(`/regions/${id}`);
      loadAll();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const getConfig = (cle) => configs.find(c => c.cle === cle)?.valeur || '';

  if (loading) return <div><div className="topbar"><div className="topbar-title">Configuration</div></div><div className="page-content"><p style={{color:'#999'}}>Chargement...</p></div></div>;

  return (
    <div>
      <div className="topbar"><div className="topbar-title">Configuration</div></div>
      <div className="page-content">

        {/* SMTP */}
        <Section title="📧 SMTP — Envoi d'emails">
          {[
            ['smtp_host', 'Serveur SMTP', 'text', false],
            ['smtp_port', 'Port', 'number', false],
            ['smtp_user', 'Login', 'text', false],
            ['smtp_password', 'Mot de passe', 'password', true],
            ['smtp_from_email', 'Email expéditeur', 'email', false],
            ['smtp_from_name', 'Nom expéditeur', 'text', false],
          ].map(([cle, label, type, secret]) => (
            <ConfigField key={cle} cle={cle} label={label} type={type} secret={secret}
              value={getConfig(cle)} onSave={handleSaveConfig} />
          ))}
          {testMsg && <div className={`alert alert-${testMsg.type}`} style={{ marginTop: 12 }}>{testMsg.text}</div>}
          <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={handleTestSmtp}>📨 Envoyer un email de test</button>
        </Section>

        {/* API */}
        <Section title="🔑 Clés API">
          <ConfigField cle="claude_api_key" label="Claude API (Anthropic)" type="text" secret={true}
            value={getConfig('claude_api_key')} onSave={handleSaveConfig} />
        </Section>

        {/* Tranches CA */}
        <Section title="📊 Tranches de CA pour le benchmark">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <p style={{ fontSize: '0.82rem', color: '#666', maxWidth: '70%' }}>
              Les adhérents sont comparés aux autres adhérents de leur tranche (basé sur le CA consolidé).
              La tranche est calculée automatiquement à chaque import de bilan.
            </p>
            <button className="btn btn-outline" style={{ fontSize: '0.72rem', padding: '6px 12px' }}
              onClick={handleRecalculer}>
              🔄 Recalculer sur tous les bilans
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Libellé</th><th>Seuil min</th><th>Seuil max</th><th>Ordre</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {tranches.filter(t => t.actif).sort((a, b) => a.ordre - b.ordre).map(t => (
                  <TrancheRow key={t.id} tranche={t} onSave={handleSaveTranche} onDelete={handleDeleteTranche} />
                ))}
                {/* Ligne d'ajout */}
                <tr style={{ background: '#fafafa' }}>
                  <td><input className="form-input" placeholder="Ex: 200k - 500k" value={newTranche.libelle}
                    onChange={e => setNewTranche({ ...newTranche, libelle: e.target.value })} style={{ margin: 0 }} /></td>
                  <td><input className="form-input" type="number" value={newTranche.seuil_min}
                    onChange={e => setNewTranche({ ...newTranche, seuil_min: parseFloat(e.target.value) })} style={{ margin: 0 }} /></td>
                  <td><input className="form-input" type="number" value={newTranche.seuil_max} placeholder="Vide = illimité"
                    onChange={e => setNewTranche({ ...newTranche, seuil_max: e.target.value })} style={{ margin: 0 }} /></td>
                  <td><input className="form-input" type="number" value={newTranche.ordre}
                    onChange={e => setNewTranche({ ...newTranche, ordre: parseInt(e.target.value) })} style={{ margin: 0, width: 60 }} /></td>
                  <td><button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                    onClick={handleCreateTranche} disabled={!newTranche.libelle}>+ Ajouter</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Régions ECF */}
        <Section title="🗺️ Régions ECF">
          <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: 16 }}>
            Les régions ECF sont proposées lors de la création d'un adhérent. Le champ région est obligatoire.
          </p>
          <div className="table-wrapper" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr><th>N°</th><th>Libellé</th><th>Statut</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {regions.sort((a, b) => a.numero - b.numero).map(r => (
                  <tr key={r.id}>
                    <td><span className="badge badge-info">RÉGION {r.numero}</span></td>
                    <td className="important">
                      {editRegion?.id === r.id
                        ? <input className="form-input" style={{ margin: 0 }} value={editRegion.libelle}
                            onChange={e => setEditRegion({ ...editRegion, libelle: e.target.value })} />
                        : r.libelle}
                    </td>
                    <td><span className={`badge ${r.actif ? 'badge-success' : 'badge-error'}`}>
                      {r.actif ? 'Active' : 'Inactive'}
                    </span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {editRegion?.id === r.id ? (
                          <>
                            <button className="btn btn-primary" style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                              onClick={() => handleUpdateRegion(r.id, editRegion.libelle, editRegion.actif)}>✓</button>
                            <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                              onClick={() => setEditRegion(null)}>✕</button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                              onClick={() => setEditRegion({ id: r.id, libelle: r.libelle, actif: r.actif })}>
                              Modifier
                            </button>
                            {r.actif && (
                              <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: '0.72rem', color: 'var(--ecf-error)' }}
                                onClick={() => handleDeleteRegion(r.id)}>
                                Désactiver
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Ligne d'ajout */}
                <tr style={{ background: 'var(--ecf-light-grey)' }}>
                  <td>
                    <input className="form-input" type="number" placeholder="N°" value={newRegion.numero}
                      onChange={e => setNewRegion({ ...newRegion, numero: e.target.value })}
                      style={{ margin: 0, width: 70 }} />
                  </td>
                  <td>
                    <input className="form-input" placeholder="Libellé de la région" value={newRegion.libelle}
                      onChange={e => setNewRegion({ ...newRegion, libelle: e.target.value })}
                      style={{ margin: 0 }} />
                  </td>
                  <td></td>
                  <td>
                    <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                      onClick={handleCreateRegion}
                      disabled={!newRegion.numero || !newRegion.libelle}>
                      + Ajouter
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Seuils scoring */}
        <Section title="⚙️ Seuils de scoring">
          <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: 12 }}>
            Définissent quand un bilan est inséré automatiquement ou mis en attente de vérification.
          </p>
          <ConfigField cle="score_seuil_auto" label="Score minimum insertion auto" type="number"
            value={getConfig('score_seuil_auto')} onSave={handleSaveConfig} />
          <ConfigField cle="score_seuil_alerte" label="Score minimum avant rejet" type="number"
            value={getConfig('score_seuil_alerte')} onSave={handleSaveConfig} />
        </Section>

      </div>
    </div>
  );
}
